import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectOrg } from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { tryUpdateOrgSettings, UpdateOrgTimezoneRequest } from '../../actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import { EditOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import dayjs from 'dayjs';

const OrgTimezoneSetting: React.FC<OrgComponentProps> = (props) => {
    const dispatch = useDispatch();
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const [modalOpen, setModalOpen] = React.useState(false);
    const [form] = Form.useForm();

    const isLoading = useIsLoading(OrgSettingsUpdatable.UpdateOrgTimezone);
    const error = useError(OrgSettingsUpdatable.UpdateOrgTimezone);
    const settingUpdated = useUpdated(OrgSettingsUpdatable.UpdateOrgTimezone);

    React.useEffect(() => {
        if (settingUpdated) {
            globalMessage.success('Organization timezone updated!');
            setModalOpen(false);
        }
    }, [settingUpdated]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    const onSave = (values: { timezone: string }) => {
        const request: UpdateOrgTimezoneRequest = {
            orgId: props.orgId,
            timezone: values.timezone,
        };
        dispatch(tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateOrgTimezone, request));
    };

    const currentTimezone = org?.timezone;
    const displayTimezone =
        currentTimezone ? `${currentTimezone} (${dayjs().tz(currentTimezone).format('z')})` : 'Not set';

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 24,
                    flex: 1,
                    minHeight: 80,
                }}
            >
                <Space direction="vertical">
                    <Text strong>Timezone: {displayTimezone}</Text>
                    <Text type="secondary">
                        Default timezone for your organization. Users can select this as their timezone preference in
                        their personal settings.
                    </Text>
                </Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setModalOpen(true)}>
                    Edit
                </Button>
            </div>
            <Modal
                title="Edit organization timezone"
                okButtonProps={{ loading: isLoading }}
                onOk={() => form.submit()}
                okText="Save"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                afterClose={() => {
                    form.resetFields();
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text>
                        Set the default timezone for your organization. This timezone will be available as an option in
                        each user's timezone preference settings.
                    </Text>
                    <Form
                        onFinish={onSave}
                        initialValues={{
                            timezone: currentTimezone || undefined,
                        }}
                        style={{ marginTop: 24, width: '100%' }}
                        form={form}
                        layout="vertical"
                    >
                        <Form.Item name="timezone" label="Timezone" style={{ width: '100%' }} labelAlign={'left'}>
                            <Select placeholder="Select a timezone" showSearch autoFocus>
                                {Intl.supportedValuesOf('timeZone').map((timezone, index) => (
                                    <Select.Option value={timezone} key={index}>
                                        {timezone} ({dayjs().tz(timezone).format('z')})
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
            </Modal>
        </>
    );
};

export default OrgTimezoneSetting;
